import {
  EFinancasModulos,
  F360Product,
  FinancasUserRoles,
  IF360Sidebar
} from "@/core/@types/types";
import F360Sidebar from "@/core/common/F360AbstractSidebar";
import { IndicadoresDashboardRouter } from "@/features/financas/views/Indicadores/router/dashboard/types";
import LogoIconBlue from "@/assets/f360-icon-blue.png";
import { inject, injectable } from "inversify";
import { EFinancasRoutes } from "@/router/financas/credenciamento/types";
import { useRegraModulosCielo } from "@/core/common/useRegraModulosCielo";
import { EminhaGestao } from "@/router/minhaGestao/types";
import { DIValidators, EFeatureFlags } from "@/core/@types/types";
import { type IF360FeatureFlagValidator } from "@/core/validators/F360FeatureFlagValidator";
import { EMenuAdmin } from "./enum/MenuAdmin";
import { EAdminViewName } from "@/router/admin/types";

@injectable()
export default class F360FinancasSidebarConfig extends F360Sidebar {
  private readonly regraModulosCielo = useRegraModulosCielo();

  private readonly hideOnCielo: boolean =
    this.regraModulosCielo?.hideModulos || false;

  @inject(DIValidators.FEATURE_FLAGS)
  private readonly featureFlag!: IF360FeatureFlagValidator;

  private readonly isLight: boolean = this.regraModulosCielo?.isLight || false;

  private validatorModulos(): boolean {
    if (
      this.hideOnCielo === false &&
      this.regraModulosCielo?.modulo === EFinancasModulos.COMPLETO
    ) {
      return true;
    }

    return this.hideOnCielo && this.isLight;
  }

  private handleMenuGestao(): Array<IF360Sidebar> {
    return [
      {
        menuLabel: this.isCielo() ? "Menu de Gestão" : "Finanças",
        img: LogoIconBlue,
        isGrouped: true,
        validator: [!this.isSysAdmin()],
        children: [
          {
            menuLabel: "Minha Gestão",
            icon: "editor_choice",
            routerConfig: {
              product: F360Product.MinhaGestao,
              routeName: EminhaGestao.Index,
              hasSubRoutes: true
            },
            validator: [
              this.featureFlag.handleValidationByName(EFeatureFlags.MinhaGestao)
            ]
          },

          {
            menuLabel: "Dashboard",
            icon: "monitoring",
            routerConfig: {
              product: F360Product.Indicadores,
              routeName: IndicadoresDashboardRouter.Index
            },
            roles: [FinancasUserRoles.Dashboard]
          },

          {
            menuLabel: "Fechamento de Caixa",
            icon: "calendar_month",
            roles: [FinancasUserRoles.FechamentoDeCaixa],
            validator: [!this.isLight],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("FechamentoDeCaixa")
            }
          },
          {
            menuLabel: !this.hideOnCielo ? "Conciliação de Cartões" : "Cartões",
            icon: "credit_card",
            roles: [FinancasUserRoles.Conciliacao],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Conciliacao")
            }
          },
          {
            menuLabel: !this.hideOnCielo
              ? "Contas a Receber"
              : "Registro de Vendas",
            icon: "add",
            roles: [FinancasUserRoles.Receita],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Receita")
            }
          },
          {
            menuLabel: "Contas a Pagar",
            icon: "remove",
            roles: [FinancasUserRoles.Despesa],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Despesa")
            }
          },
          {
            menuLabel: "Nota Fiscal Eletrônica",
            icon: "barcode",
            roles: [FinancasUserRoles.Nfe],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("NFe")
            }
          },
          {
            menuLabel: "Plano Orçamentário",
            icon: "hourglass_top",
            roles: [FinancasUserRoles.PlanoOrcamentario],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("PlanoOrcamentario")
            }
          },
          {
            menuLabel: "Fluxo de Caixa",
            icon: "point_of_sale",
            roles: [FinancasUserRoles.FluxoDeCaixa],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("FluxoDeCaixa")
            }
          },
          {
            menuLabel: "Contas Movimento",
            icon: "account_balance",
            roles: [FinancasUserRoles.ContasMovimento],
            validator: [!this.isLight],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("ContasMovimento")
            }
          },
          {
            menuLabel: "DRE",
            icon: "attach_money",
            roles: [FinancasUserRoles.DRE],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("DRE")
            }
          },
          {
            menuLabel: "Diagnóstico",
            icon: "cardiology",
            roles: [FinancasUserRoles.Diagnostico],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Diagnostico")
            }
          },
          {
            menuLabel: "Upload de Arquivos",
            icon: "cloud_upload",
            roles: [FinancasUserRoles.Arquivo],
            validator: [!this.hideOnCielo],
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Arquivo")
            }
          }
        ]
      }
    ];
  }

  private handleMenuCadastro(): Array<IF360Sidebar> {
    return [
      {
        menuLabel: "Menu de cadastro",
        isGrouped: true,
        validator: [!this.isSysAdmin(), this.isCielo()],
        children: [
          {
            menuLabel: "Empresas e Filiais",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Empresa")
            },
            icon: "business_center"
          },
          {
            menuLabel: "Credenciamento",
            routerConfig: {
              product: F360Product.FinancasNewHorizons,
              routeName: EFinancasRoutes.credenciamento
            },
            icon: "credit_card"
          },
          {
            menuLabel: "Centros de Custo",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("CentroDeCusto")
            },
            icon: "apartment",
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Clientes e Fornecedores",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Pessoa")
            },
            icon: "contacts",
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Planos de Contas",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("PlanoDeConta")
            },
            icon: "table_chart",
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Contas",
            icon: "account_balance",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Conta")
            }
          },
          {
            menuLabel: "Regras de Rateio",
            icon: "pie_chart",

            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("RegraDeRateio")
            },
            validator: [!this.hideOnCielo]
          },
          {
            menuLabel: "Integrações",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Webservice")
            },
            icon: "electrical_services"
          },
          {
            menuLabel: "Upload de arquivos",
            icon: "cloud_upload",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Arquivo")
            }
          },
          {
            menuLabel: "Relatórios",
            icon: "cloud_download",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Relatorio")
            }
          }
        ]
      }
    ];
  }

  private handleMenuAdmin(): Array<IF360Sidebar> {
    return [
      {
        menuLabel: "Finanças",
        img: LogoIconBlue,
        isGrouped: true,
        roles: [FinancasUserRoles.FechamentoDeCaixa],
        validator: [this.isSysAdmin()],
        children: [
          {
            menuLabel: EMenuAdmin.CLIENTES,
            icon: "groups",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Cliente")
            }
          },
          {
            menuLabel: EMenuAdmin.USUARIOS,
            icon: "person",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Usuario")
            }
          },
          {
            menuLabel: EMenuAdmin.VAN_CONFIG,
            icon: "group",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("VanConfig")
            }
          },
          {
            menuLabel: EMenuAdmin.LEAD,
            icon: "rocket_launch",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("OnboardingLead")
            }
          },
          {
            menuLabel: EMenuAdmin.INTEGRACOES,
            icon: "integration_instructions",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("Integracoes")
            }
          },
          {
            menuLabel: EMenuAdmin.REGRAS_CO,
            icon: "settings",
            routerConfig: {
              product: F360Product.Financas,
              routeName: this.handleFinancasURL("RegrasCO")
            }
          },
          {
            menuLabel: EMenuAdmin.NOTIFICACOES,
            icon: "notifications",
            routerConfig: {
              product: F360Product.Admin,
              routeName: EAdminViewName.NOTIFICACOES
            }
          }
        ]
      }
    ];
  }

  handleSidebar(): Array<IF360Sidebar> {
    return [
      ...this.handleMenuGestao(),
      ...this.handleMenuCadastro(),
      ...this.handleMenuAdmin()
    ];
  }
}
